import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { getApiBaseUrl } from './api';
import type {
  NotificationChannel,
  NotificationTemplate,
  NotificationGroup,
  Notification,
  NotificationCampaign,
  SendNotificationRequest,
  PaginatedResponse,
  NotificationTopic,
} from '../models';

export const connectApi = createApi({
  reducerPath: 'connectApi',
  baseQuery: (args, api, extraOptions) =>
    fetchBaseQuery({
      baseUrl: `${getApiBaseUrl()}/connect`,
      prepareHeaders: (headers) => {
        const token = localStorage.getItem('token');
        if (token) {
          headers.set('Authorization', `Bearer ${token}`);
        }
        return headers;
      },
    })(args, api, extraOptions),
  tagTypes: [
    'NotificationChannel',
    'NotificationTemplate',
    'NotificationGroup',
    'Notification',
    'NotificationCampaign',
    'NotificationTopic', // Added NotificationTopic tag
  ],
  endpoints: (builder) => ({
    // --- Pagination-aware endpoints ---
    // Notification Channels (paginated)
    getNotificationChannels: builder.query<PaginatedResponse<NotificationChannel>, { page?: number; page_size?: number }>({
      query: (params) => {
        const search = new URLSearchParams({
          ...(params?.page ? { page: String(params.page) } : {}),
          ...(params?.page_size ? { page_size: String(params.page_size) } : {}),
        }).toString();
        return `channels/${search ? `?${search}` : ''}`;
      },
      providesTags: ['NotificationChannel'],
    }),
    getNotificationChannel: builder.query<NotificationChannel, string>({
      query: (id) => `channels/${id}/`,
      providesTags: (_, __, id) => [{ type: 'NotificationChannel', id }],
    }),
    createNotificationChannel: builder.mutation<NotificationChannel, Partial<NotificationChannel>>({
      query: (channel) => ({
        url: 'channels/',
        method: 'POST',
        body: channel,
      }),
      invalidatesTags: ['NotificationChannel'],
    }),
    updateNotificationChannel: builder.mutation<NotificationChannel, { id: string; data: Partial<NotificationChannel> }>({
      query: ({ id, data }) => ({
        url: `channels/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_, __, { id }) => [{ type: 'NotificationChannel', id }],
    }),
    deleteNotificationChannel: builder.mutation<void, string>({
      query: (id) => ({
        url: `channels/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['NotificationChannel'],
    }),
    // Notification Templates (paginated)
    getNotificationTemplates: builder.query<PaginatedResponse<NotificationTemplate>, { page?: number; page_size?: number; search?: string }>({
      query: (params) => {
        const search = new URLSearchParams({
          ...(params?.page ? { page: String(params.page) } : {}),
          ...(params?.page_size ? { page_size: String(params.page_size) } : {}),
          ...(params?.search ? { search: params.search } : {}),
        }).toString();
        return `templates/${search ? `?${search}` : ''}`;
      },
      providesTags: ['NotificationTemplate'],
    }),
    getNotificationTemplate: builder.query<NotificationTemplate, string>({
      query: (id) => `templates/${id}/`,
      providesTags: (_, __, id) => [{ type: 'NotificationTemplate', id }],
    }),
    createNotificationTemplate: builder.mutation<NotificationTemplate, Partial<NotificationTemplate>>({
      query: (template) => ({
        url: 'templates/',
        method: 'POST',
        body: template,
      }),
      invalidatesTags: ['NotificationTemplate'],
    }),
    updateNotificationTemplate: builder.mutation<NotificationTemplate, { id: string; data: Partial<NotificationTemplate> }>({
      query: ({ id, data }) => ({
        url: `templates/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_, __, { id }) => [{ type: 'NotificationTemplate', id }],
    }),
    deleteNotificationTemplate: builder.mutation<void, string>({
      query: (id) => ({
        url: `templates/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['NotificationTemplate'],
    }),
    
    // Notification Groups (paginated)
    getNotificationGroups: builder.query<PaginatedResponse<NotificationGroup>, { page?: number; page_size?: number }>({
      query: (params) => {
        const search = new URLSearchParams({
          ...(params?.page ? { page: String(params.page) } : {}),
          ...(params?.page_size ? { page_size: String(params.page_size) } : {}),
        }).toString();
        return `groups/${search ? `?${search}` : ''}`;
      },
      providesTags: ['NotificationGroup'],
    }),
    getNotificationGroup: builder.query<NotificationGroup, string>({
      query: (id) => `groups/${id}/?detailed=true`,
      providesTags: (_, __, id) => [{ type: 'NotificationGroup', id }],
    }),
    createNotificationGroup: builder.mutation<NotificationGroup, Partial<NotificationGroup>>({
      query: (group) => ({
        url: 'groups/',
        method: 'POST',
        body: group,
      }),
      invalidatesTags: ['NotificationGroup'],
    }),
    updateNotificationGroup: builder.mutation<NotificationGroup, { id: string; data: Partial<NotificationGroup> }>({
      query: ({ id, data }) => ({
        url: `groups/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_, __, { id }) => [{ type: 'NotificationGroup', id }],
    }),
    deleteNotificationGroup: builder.mutation<void, string>({
      query: (id) => ({
        url: `groups/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['NotificationGroup'],
    }),
    previewQueryFilter: builder.mutation<{ total_users: number; sample_users: any[]; message: string }, { query_filter: any }>({
      query: (data) => ({
        url: 'groups/preview_query_filter/',
        method: 'POST',
        body: data,
      }),
    }),
    previewGroupUsers: builder.query<{ total_users: number; users: any[]; page: number; page_size: number; has_next: boolean; has_previous: boolean }, { id: string; page?: number; page_size?: number }>({
      query: ({ id, page = 1, page_size = 20 }) => {
        const params = new URLSearchParams({
          page: String(page),
          page_size: String(page_size),
        });
        return `groups/${id}/preview_users/?${params.toString()}`;
      },
    }),

    // Test notification channel
    testNotificationChannel: builder.mutation<{ status: string; message: string }, { id: string; data: { fcm_token?: string; template_id?: string; test_title?: string; test_body?: string; } }>({
      query: ({ id, data }) => ({
        url: `channels/${id}/test/`,
        method: 'POST',
        body: data,
      }),
    }),
  
  // Notifications (paginated)
    getNotifications: builder.query<PaginatedResponse<Notification>, { page?: number; page_size?: number; search?: string }>({
      query: (params) => {
        const search = new URLSearchParams({
          ...(params?.page ? { page: String(params.page) } : {}),
          ...(params?.page_size ? { page_size: String(params.page_size) } : {}),
          ...(params?.search ? { search: params.search } : {}),
        }).toString();
        return `notifications/${search ? `?${search}` : ''}`;
      },
      providesTags: ['Notification'],
    }),
    getNotification: builder.query<Notification, string>({
      query: (id) => `notifications/${id}/`,
      providesTags: (_, __, id) => [{ type: 'Notification', id }],
    }),
    markNotificationAsRead: builder.mutation<void, string>({
      query: (id) => ({
        url: `notifications/${id}/mark_as_read/`,
        method: 'POST',
      }),
      invalidatesTags: (_, __, id) => [{ type: 'Notification', id }],
    }),

    // Notification Campaigns (paginated)
    getNotificationCampaigns: builder.query<PaginatedResponse<NotificationCampaign>, { page?: number; page_size?: number }>({
      query: (params) => {
        const search = new URLSearchParams({
          ...(params?.page ? { page: String(params.page) } : {}),
          ...(params?.page_size ? { page_size: String(params.page_size) } : {}),
        }).toString();
        return `campaigns/${search ? `?${search}` : ''}`;
      },
      providesTags: ['NotificationCampaign'],
    }),
    getNotificationCampaign: builder.query<NotificationCampaign, string>({
      query: (id) => `campaigns/${id}/`,
      providesTags: (_, __, id) => [{ type: 'NotificationCampaign', id }],
    }),
    createNotificationCampaign: builder.mutation<NotificationCampaign, Partial<NotificationCampaign>>({
      query: (campaign) => ({
        url: 'campaigns/',
        method: 'POST',
        body: campaign,
      }),
      invalidatesTags: ['NotificationCampaign'],
    }),
    updateNotificationCampaign: builder.mutation<NotificationCampaign, { id: string; data: Partial<NotificationCampaign> }>({
      query: ({ id, data }) => ({
        url: `campaigns/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_, __, { id }) => [{ type: 'NotificationCampaign', id }],
    }),
    deleteNotificationCampaign: builder.mutation<void, string>({
      query: (id) => ({
        url: `campaigns/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['NotificationCampaign'],
    }),
    scheduleNotificationCampaign: builder.mutation<void, { id: string; scheduledAt?: string }>({
      query: ({ id, scheduledAt }) => ({
        url: `campaigns/${id}/schedule/`,
        method: 'POST',
        body: scheduledAt ? { scheduled_at: scheduledAt } : {},
      }),
      invalidatesTags: (_, __, { id }) => [{ type: 'NotificationCampaign', id }],
    }),
    cancelNotificationCampaign: builder.mutation<void, string>({
      query: (id) => ({
        url: `campaigns/${id}/cancel/`,
        method: 'POST',
      }),
      invalidatesTags: (_, __, id) => [{ type: 'NotificationCampaign', id }],
    }),

    // Send Immediate Notification
    sendNotification: builder.mutation<{ status: string; sent_count: number }, SendNotificationRequest>({
      query: (data) => ({
        url: 'send/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Notification'],
    }),

    // Notification Topics (paginated)
    getNotificationTopics: builder.query<PaginatedResponse<NotificationTopic>, { page?: number; page_size?: number }>({
      query: (params) => {
        const search = new URLSearchParams({
          ...(params?.page ? { page: String(params.page) } : {}),
          ...(params?.page_size ? { page_size: String(params.page_size) } : {}),
        }).toString();
        return `notification-topics/${search ? `?${search}` : ''}`;
      },
      providesTags: ['NotificationTopic'],
    }),
    getNotificationTopic: builder.query<NotificationTopic, string>({
      query: (id) => `notification-topics/${id}/`,
      providesTags: (_, __, id) => [{ type: 'NotificationTopic', id }],
    }),
    createNotificationTopic: builder.mutation<NotificationTopic, Partial<NotificationTopic>>({
      query: (topic) => ({
        url: 'notification-topics/',
        method: 'POST',
        body: topic,
      }),
      invalidatesTags: ['NotificationTopic'],
    }),
    updateNotificationTopic: builder.mutation<NotificationTopic, { id: string; data: Partial<NotificationTopic> }>({
      query: ({ id, data }) => ({
        url: `notification-topics/${id}/`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_, __, { id }) => [{ type: 'NotificationTopic', id }],
    }),
    deleteNotificationTopic: builder.mutation<void, string>({
      query: (id) => ({
        url: `notification-topics/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['NotificationTopic'],
    }),
    sendTopicNotification: builder.mutation<{ status: string; sent_count: number }, { 
      topic_id: string; 
      template_id: string; 
      channel_id: string;
      scheduled_at?: string | null;
      template_variables?: Record<string, string>;
    }>({
      query: (data) => ({
        url: 'notification-topics/send/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['NotificationTopic', 'Notification'],
    }),
  }),
});

export const {
  useGetNotificationChannelsQuery,
  useGetNotificationChannelQuery,
  useCreateNotificationChannelMutation,
  useUpdateNotificationChannelMutation,
  useDeleteNotificationChannelMutation,
  useTestNotificationChannelMutation,
  
  useGetNotificationTemplatesQuery,
  useGetNotificationTemplateQuery,
  useCreateNotificationTemplateMutation,
  useUpdateNotificationTemplateMutation,
  useDeleteNotificationTemplateMutation,
  
  useGetNotificationGroupsQuery,
  useGetNotificationGroupQuery,
  useCreateNotificationGroupMutation,
  useUpdateNotificationGroupMutation,
  useDeleteNotificationGroupMutation,
  usePreviewQueryFilterMutation,
  usePreviewGroupUsersQuery,
  
  useGetNotificationsQuery,
  useGetNotificationQuery,
  useMarkNotificationAsReadMutation,
  
  useGetNotificationCampaignsQuery,
  useGetNotificationCampaignQuery,
  useCreateNotificationCampaignMutation,
  useUpdateNotificationCampaignMutation,
  useDeleteNotificationCampaignMutation,
  useScheduleNotificationCampaignMutation,
  useCancelNotificationCampaignMutation,
  
  useSendNotificationMutation,

  // Added notification topic hooks
  useGetNotificationTopicsQuery,
  useGetNotificationTopicQuery,
  useCreateNotificationTopicMutation,
  useUpdateNotificationTopicMutation,
  useDeleteNotificationTopicMutation,
  useSendTopicNotificationMutation,
} = connectApi;
